import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

/**
 * Exportador mensual -> XLSX/CSV
 * - Recorre 12 meses del año indicado
 * - Paginación por month hasta agotar resultados
 * - Descarga cada mes al terminar (progresivo)
 *
 * Props:
 *  - collectionPath: string = "orders"
 *  - dateField: string = "createdAt"
 *  - pageSize: number = 1000   (sube/baja según tu índice y costos)
 *  - year: number | undefined  (si no se setea, usa el actual)
 */
const FirestoreMonthlyExporter = ({
  collectionPath = "orders",
  dateField = "createdAt",
  pageSize = 1000,
  year: initialYear,
  columns, // <- opcional: ['id','_createdAtISO','status','...']
  maxRowsPerFile = 10000, // <- chunk por archivo
  forceCsvOver = 20000, // <- si filas > N, exporta CSV (no XLSX)
}) => {
  const [year, setYear] = useState(
    typeof initialYear === "number" ? initialYear : new Date().getFullYear()
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const cancelRef = useRef(false);

  // progreso por mes (0..11)
  const [progress, setProgress] = useState(
    Array.from({ length: 12 }, () => ({
      status: "pending", // pending | running | done | error | skipped
      pages: 0,
      rows: 0,
      message: "",
    }))
  );

  const monthNames = useMemo(
    () => [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ],
    []
  );

  const normalizeRow = (row, cols) => {
    if (!cols || !cols.length) return row;
    const out = {};
    for (const c of cols) {
      const v = row[c];
      out[c] =
        v && typeof v === "object"
          ? JSON.stringify(v)
          : Array.isArray(v)
          ? JSON.stringify(v)
          : v ?? "";
    }
    return out;
  };

  const tsRangeForMonth = (y, m /* 0-based */) => {
    const startDate = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
    const endDate = new Date(Date.UTC(y, m + 1, 1, 0, 0, 0, 0)); // exclusivo
    return {
      start: Timestamp.fromDate(startDate),
      end: Timestamp.fromDate(endDate),
    };
  };

  const baseQuery = useCallback(
    (range, startAfterDoc = null) => {
      const col = collection(db, collectionPath);
      const parts = [
        where(dateField, ">=", range.start),
        where(dateField, "<", range.end), // mejor "< nextMonth"
        orderBy(dateField, "asc"),
      ];
      if (startAfterDoc) {
        return query(col, ...parts, startAfter(startAfterDoc), limit(pageSize));
      }
      return query(col, ...parts, limit(pageSize));
    },
    [collectionPath, dateField, pageSize]
  );

  const mapDoc = (docSnap) => {
    const data = docSnap.data() || {};
    const ts = data[dateField];
    const iso =
      ts && typeof ts.toDate === "function"
        ? ts.toDate().toISOString()
        : data[dateField] || "";
    return { id: docSnap.id, ...data, _createdAtISO: iso };
  };

  // Descarga XLSX (si hay 'xlsx') o CSV como fallback
  const downloadMonthFile = async (rows, filename) => {
    if (!rows.length) {
      // Genera un CSV vacío con encabezado mínimo
      const header = "id,_createdAtISO\n";
      const blob = new Blob([header], { type: "text/csv;charset=utf-8;" });
      triggerDownload(blob, `${filename}.csv`);
      return;
    }

    const columns = Array.from(
      rows.reduce((set, row) => {
        Object.keys(row).forEach((k) => set.add(k));
        return set;
      }, new Set(["id", "_createdAtISO"]))
    );

    try {
      // intento XLSX dinámico
      const xlsx = await import("xlsx").catch(() => null);
      if (xlsx && xlsx.utils && xlsx.writeFile) {
        // Normaliza filas con todas las columnas presentes
        const normalized = rows.map((r) => {
          const obj = {};
          for (const c of columns) {
            const v = r[c];
            obj[c] =
              v && typeof v === "object" && !Array.isArray(v)
                ? JSON.stringify(v)
                : Array.isArray(v)
                ? JSON.stringify(v)
                : v ?? "";
          }
          return obj;
        });
        const ws = xlsx.utils.json_to_sheet(normalized, { header: columns });
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, "Datos");
        xlsx.writeFile(wb, `${filename}.xlsx`);
        return;
      }
      // fallback CSV
      const csvHeader = columns.join(";");
      const esc = (val) => {
        if (val === null || val === undefined) return "";
        const s =
          typeof val === "object" ? JSON.stringify(val) : String(val ?? "");
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = rows.map((r) => columns.map((c) => esc(r[c])).join(";"));
      const csv = [csvHeader, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      triggerDownload(blob, `${filename}.csv`);
    } catch {
      // fallback CSV ante cualquier error
      const csvHeader = columns.join(";");
      const esc = (val) => {
        if (val === null || val === undefined) return "";
        const s =
          typeof val === "object" ? JSON.stringify(val) : String(val ?? "");
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = rows.map((r) => columns.map((c) => esc(r[c])).join(";"));
      const csv = [csvHeader, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      triggerDownload(blob, `${filename}.csv`);
    }
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateMonthProgress = (m, patch) => {
    setProgress((prev) => {
      const next = prev.map((p, i) => (i === m ? { ...p, ...patch } : p));
      return next;
    });
  };

  const exportYear = async () => {
    setError("");
    setRunning(true);
    cancelRef.current = false;

    // reset progreso
    setProgress(
      Array.from({ length: 12 }, () => ({
        status: "pending",
        pages: 0,
        rows: 0,
        message: "",
      }))
    );

    try {
      for (let m = 0; m < 12; m++) {
        if (cancelRef.current) break;
        updateMonthProgress(m, {
          status: "running",
          pages: 0,
          rows: 0,
          message: "",
        });

        const range = tsRangeForMonth(year, m);
        let monthRows = [];
        let last = null;
        let pages = 0;
        let total = 0;

        while (true) {
          if (cancelRef.current) {
            updateMonthProgress(m, {
              status: "skipped",
              message: "cancelado por el usuario",
            });
            break;
          }
          let snap;
          try {
            snap = await getDocs(baseQuery(range, last));
          } catch (e) {
            // índice faltante u otro error
            const msg =
              e?.code === "failed-precondition"
                ? `Falta índice compuesto para (${dateField})`
                : e?.message || "Error de consulta";
            updateMonthProgress(m, { status: "error", message: msg });
            break;
          }

          if (!snap.size) {
            // sin más páginas
            break;
          }

          const docs = snap.docs.map(mapDoc);
          monthRows = monthRows.concat(docs);
          last = snap.docs[snap.docs.length - 1];
          pages += 1;
          total += docs.length;

          updateMonthProgress(m, { pages, rows: total });

          if (snap.size < pageSize) {
            // última página del mes
            break;
          }
        }

        if (cancelRef.current) break;

        // Descarga del mes (aunque esté vacío generamos archivo con encabezado)
        const yyyy = String(year);
        const mm = String(m + 1).padStart(2, "0");
        const filename = `orders_${yyyy}-${mm}`;
        await downloadMonthFile(monthRows, filename);

        // marca done si no hubo error previo
        const statusEnd = progress[m]?.status === "error" ? "error" : "done";
        updateMonthProgress(m, { status: statusEnd });
      }
    } finally {
      setRunning(false);
    }
  };

  const cancel = () => {
    cancelRef.current = true;
  };

  const canRun = useMemo(() => !running, [running]);

  return (
    <div style={{ display: "grid", gap: 12, padding: 16 }}>
      <h2 style={{ margin: 0 }}>Exportar reportes mensuales del año</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <label>
          Año:&nbsp;
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 110 }}
          />
        </label>

        <label>
          Colección:&nbsp;
          <code>{collectionPath}</code>
        </label>

        <label>
          Fecha:&nbsp;
          <code>{dateField}</code>
        </label>

        <label>
          Page size:&nbsp;
          <input
            type="number"
            value={pageSize}
            onChange={(e) => {
              // (opcional) controlar en padre con prop si quieres
              // aquí queda fijo por props; este input es decorativo
            }}
            style={{ width: 90 }}
            disabled
            title="Configúralo vía prop pageSize"
          />
        </label>

        <button onClick={exportYear} disabled={!canRun}>
          {running ? "Exportando..." : "Exportar año (12 archivos)"}
        </button>
        {running && (
          <button onClick={cancel} style={{ color: "#b91c1c" }}>
            Cancelar
          </button>
        )}
      </div>

      {!!error && <div style={{ color: "crimson", fontSize: 14 }}>{error}</div>}

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
        >
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              <th style={th}>Mes</th>
              <th style={th}>Estado</th>
              <th style={th}>Páginas</th>
              <th style={th}>Filas</th>
              <th style={th}>Mensaje</th>
            </tr>
          </thead>
          <tbody>
            {progress.map((p, i) => (
              <tr key={i}>
                <td style={td}>{monthNames[i]}</td>
                <td style={td}>
                  {p.status === "pending" && "Pendiente"}
                  {p.status === "running" && "Procesando..."}
                  {p.status === "done" && "Completado"}
                  {p.status === "error" && "Error"}
                  {p.status === "skipped" && "Cancelado"}
                </td>
                <td style={td}>{p.pages}</td>
                <td style={td}>{p.rows}</td>
                <td
                  style={{
                    ...td,
                    color: p.status === "error" ? "#b91c1c" : undefined,
                  }}
                >
                  {p.message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <small style={{ opacity: 0.7 }}>
        Nota: la consulta usa <code>where({dateField} &ge; inicioMes)</code> y{" "}
        <code>where({dateField} &lt; siguienteMes)</code> +{" "}
        <code>orderBy({dateField})</code>. Si Firestore solicita índice
        compuesto, créalo desde el enlace que muestra la consola.
      </small>
    </div>
  );
};

const th = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #e5e7eb",
};
const td = {
  padding: "8px 10px",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "top",
};

export default FirestoreMonthlyExporter;
